import React, { useState, useEffect } from 'react';
import { addDocument, getCollection, findDocumentsByField, updateDocument } from '../services/firestoreService';
import { validateRut } from '../utils/validators';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  MenuItem,
  InputAdornment,
  Container
} from '@mui/material';
import { REGIONES_COMUNAS } from '../utils/regionData';

const CAMPUS_OPTIONS = [
  'Providencia',
  'Maipú',
  'Santiago Centro',
  'La Florida',
  'Melipilla',
  'Campus Online'
];

const initialFormState = {
  nombreCompleto: '',
  campus: '',
  ownerRut: '',
  ownerCelular: '',
  ownerMail: '',
  ownerRegion: 'Región Metropolitana',
  ownerComuna: '',
  ownerConvenio: '',
  patientName: '',
  species: '',
  breed: '',
  genero: '',
  birthDate: '',
  attentionNeed: '',
  priority: '',
  additionalInfo: '',
};

const PublicUDLARequest = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [needs, setNeeds] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [rutError, setRutError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const loadAuxData = async () => {
      try {
        const [needsData, conveniosData, breedsData] = await Promise.all([
          getCollection('needs'),
          getCollection('convenios'),
          getCollection('breeds')
        ]);
        
        // Debug: Ver todos los convenios cargados
        console.log('📋 Total convenios cargados:', conveniosData.length);
        console.log('📋 Convenios completos:', conveniosData);
        
        // Filtrar convenios
        const filteredConvenios = conveniosData.filter(c => {
          const isActive = c.isActive !== false;
          const isComunidadUDLA = c.type === 'Comunidad UDLA';
          
          console.log(`Convenio: "${c.name}" - type: "${c.type}" - isActive: ${c.isActive} - Incluir: ${isActive && isComunidadUDLA}`);
          
          return isActive && isComunidadUDLA;
        });
        
        console.log('✅ Convenios filtrados para Comunidad UDLA:', filteredConvenios.length);
        console.log('✅ Convenios filtrados:', filteredConvenios);
        
        // Filtrar necesidades activas
        const filteredNeeds = needsData.filter(n => n.isActive !== false);
        console.log('📋 Total necesidades activas:', filteredNeeds.length);
        
        // Log de razas
        console.log('📋 Total razas cargadas:', breedsData?.length || 0);
        console.log('📋 Razas por especie:', {
          caninos: breedsData?.filter(b => b.species === 'Canino').length || 0,
          felinos: breedsData?.filter(b => b.species === 'Felino').length || 0
        });
        
        setNeeds(filteredNeeds);
        setConvenios(filteredConvenios);
        setBreeds(breedsData || []);
      } catch (err) {
        console.error('Error loading needs/convenios/breeds:', err);
      }
    };
    loadAuxData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      if (name === 'species') {
        updated.breed = '';
      }

      if (name === 'ownerRegion') {
        updated.ownerComuna = '';
      }

      return updated;
    });

    if (name === 'ownerRut') {
      if (value && !validateRut(value)) {
        setRutError('El RUT ingresado no es válido.');
      } else {
        setRutError('');
      }
    }

    if (name === 'ownerCelular') {
      const digits = value.replace(/\D/g, '');
      if (!/^9\d{8}$/.test(digits)) {
        setPhoneError('Ingresa un celular válido de 9 dígitos, por ejemplo 9XXXXXXXX');
      } else {
        setPhoneError('');
      }
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDifference = today.getMonth() - birth.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age.toString();
  };

  const normalizeName = (name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rutError) {
      setError('Por favor, corrija los errores en el RUT antes de continuar.');
      return;
    }

    if (phoneError) {
      setError('Por favor, corrija el teléfono antes de continuar.');
      return;
    }

    const requiredFields = [
      'nombreCompleto',
      'campus',
      'ownerRut',
      'ownerCelular',
      'ownerMail',
      'ownerRegion',
      'ownerComuna',
      'ownerConvenio',
      'patientName',
      'species',
      'breed',
      'genero',
      'birthDate',
      'attentionNeed',
      'priority',
      'additionalInfo',
    ];

    const missingFields = requiredFields.filter((field) => !String(formData[field] || '').trim());
    if (missingFields.length > 0) {
      setError('Por favor completa todos los campos obligatorios del formulario.');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // 1. Buscar o crear propietario
      const phoneDigits = formData.ownerCelular.replace(/\D/g, '');
      const celularFull = phoneDigits ? `+56${phoneDigits}` : '';

      const ownerData = {
        name: formData.nombreCompleto.trim(),
        rut: formData.ownerRut.trim(),
        celular: celularFull,
        mail: formData.ownerMail.trim(),
        contact: formData.ownerMail.trim(),
        region: formData.ownerRegion.trim(),
        comuna: formData.ownerComuna.trim(),
        convenio: formData.ownerConvenio || '',
        campus: formData.campus || '',
      };

      // Buscar owner por RUT
      const existingOwners = await findDocumentsByField('owners', 'rut', formData.ownerRut.trim());
      let ownerId;

      if (existingOwners.length > 0) {
        // Actualizar owner existente
        ownerId = existingOwners[0].id;
        await updateDocument('owners', ownerId, ownerData);
      } else {
        // Crear nuevo owner
        ownerId = await addDocument('owners', ownerData);
      }

      // 2. Buscar o crear paciente
      const edad = calculateAge(formData.birthDate);
      const patientData = {
        name: formData.patientName.trim(),
        species: formData.species,
        breed: formData.breed.trim(),
        genero: formData.genero || '',
        birthDate: formData.birthDate || '',
        edad,
        ownerId,
      };

      // Buscar pacientes del owner
      const existingPatients = await findDocumentsByField('patients', 'ownerId', ownerId);
      const normalizedInputName = normalizeName(formData.patientName.trim());
      
      let patientId;
      const matchingPatient = existingPatients.find(p => 
        normalizeName(p.name) === normalizedInputName
      );

      if (matchingPatient) {
        // Actualizar paciente existente
        patientId = matchingPatient.id;
        await updateDocument('patients', patientId, patientData);
      } else {
        // Crear nuevo paciente
        patientId = await addDocument('patients', patientData);
      }

      // 3. Crear solicitud de contacto
      const requestData = {
        source: 'Comunidad UDLA',
        ownerName: ownerData.name,
        patientName: patientData.name,
        contactInfo: celularFull || ownerData.mail || '',
        attentionNeed: formData.attentionNeed || '',
        patientType: formData.ownerConvenio || 'Sin Convenio',
        assignedTo: '',
        ownerId,
        patientId,
        createdAt: new Date(),
        status: 'Nuevo',
        priority: formData.priority || 'Media',
        additionalInfo: formData.additionalInfo || '',
        campus: formData.campus || '',
      };

      const docId = await addDocument('contact-requests', requestData);
      setSuccess(`Tu solicitud fue enviada con éxito. ID de referencia: ${docId}`);

      setFormData(initialFormState);
      setRutError('');
      setPhoneError('');
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('Error al crear la solicitud. Inténtelo de nuevo.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const comunasOptions = formData.ownerRegion ? REGIONES_COMUNAS[formData.ownerRegion] || [] : [];
  const filteredBreeds = formData.species
    ? breeds.filter((b) => b.species === formData.species)
    : [];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#f5f5f5',
      py: { xs: 2, sm: 3, md: 4 },
      px: { xs: 1, sm: 2 }
    }}>
      <Container maxWidth="md">
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 2, sm: 3 } }}>
            <img 
              src={`${process.env.PUBLIC_URL}/Logo-CDC.png`}
              alt="Logo Hospital Clínico Veterinario UDLA" 
              style={{ 
                maxWidth: '250px',
                width: '100%',
                height: 'auto'
              }} 
            />
          </Box>
          <Typography 
            variant="h4" 
            gutterBottom 
            align="center" 
            sx={{ 
              mb: { xs: 2, sm: 3 }, 
              color: 'primary.main', 
              fontWeight: 600,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
            }}
          >
            Solicitud Atención Veterinaria - Comunidad UDLA
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            gutterBottom 
            align="center" 
            sx={{ 
              mb: { xs: 2, sm: 3, md: 4 },
              fontSize: { xs: '0.875rem', sm: '1rem' }
            }}
          >
            Complete el siguiente formulario para solicitar atención veterinaria.
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}
            
            {/* Alerta si no hay datos cargados */}
            {(convenios.length === 0 || needs.length === 0 || breeds.length === 0) && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Atención:</strong> Algunos datos del formulario no están disponibles.
                {convenios.length === 0 && <div>• No hay convenios de "Comunidad UDLA" configurados.</div>}
                {needs.length === 0 && <div>• No hay necesidades de atención configuradas.</div>}
                {breeds.length === 0 && <div>• No hay razas configuradas.</div>}
                <div style={{ marginTop: '8px' }}>Por favor, contacte al administrador o revise la consola del navegador (F12) para más detalles.</div>
              </Alert>
            )}

            {/* Datos Iniciales */}
            <Typography variant="h6" sx={{ mt: { xs: 1, sm: 2 }, mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Información del Solicitante</Typography>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  label="Nombre Completo *"
                  name="nombreCompleto"
                  value={formData.nombreCompleto}
                  onChange={handleChange}
                  required
                  placeholder="Ingrese su nombre completo"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  select
                  label="Campus"
                  name="campus"
                  value={formData.campus}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="">Seleccionar campus...</MenuItem>
                  {CAMPUS_OPTIONS.map((campus) => (
                    <MenuItem key={campus} value={campus}>
                      {campus}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  select
                  label="Convenio *"
                  name="ownerConvenio"
                  value={formData.ownerConvenio}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    convenios.length === 0
                      ? 'No hay convenios de Comunidad UDLA disponibles'
                      : `${convenios.length} convenios disponibles`
                  }
                >
                  <MenuItem value="">Seleccionar convenio...</MenuItem>
                  {convenios
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((conv) => (
                      <MenuItem key={conv.id} value={conv.name}>
                        {conv.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Datos del Tutor */}
            <Typography variant="h6" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Datos de Contacto</Typography>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  fullWidth
                  label="RUT *"
                  name="ownerRut"
                  value={formData.ownerRut}
                  onChange={handleChange}
                  required
                  error={!!rutError}
                  helperText={rutError || 'Formato: 12345678-9'}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  fullWidth
                  label="Celular *"
                  name="ownerCelular"
                  value={formData.ownerCelular}
                  onChange={handleChange}
                  required
                  type="tel"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+56</InputAdornment>,
                  }}
                  InputLabelProps={{ shrink: true }}
                  error={!!phoneError}
                  helperText={phoneError || 'Ingresa un número de 9 dígitos, ej: 9XXXXXXXX'}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 4
                }}>
                <TextField
                  fullWidth
                  label="Email de Contacto *"
                  name="ownerMail"
                  value={formData.ownerMail}
                  onChange={handleChange}
                  type="email"
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  select
                  label="Región"
                  name="ownerRegion"
                  value={formData.ownerRegion}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">Selecciona tu región...</MenuItem>
                  {Object.keys(REGIONES_COMUNAS).map((region) => (
                    <MenuItem key={region} value={region}>
                      {region}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 6
                }}>
                <TextField
                  fullWidth
                  select
                  label="Comuna"
                  name="ownerComuna"
                  value={formData.ownerComuna}
                  onChange={handleChange}
                  required
                  disabled={!formData.ownerRegion}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 150 }}
                >
                  <MenuItem value="">Selecciona tu comuna...</MenuItem>
                  {comunasOptions.map((comuna) => (
                    <MenuItem key={comuna} value={comuna}>
                      {comuna}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Datos del Paciente */}
            <Typography variant="h6" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Datos del Paciente</Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Nombre del Paciente *"
                  name="patientName"
                  value={formData.patientName}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  select
                  label="Especie"
                  name="species"
                  value={formData.species}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="">Canino o Felino...</MenuItem>
                  <MenuItem value="Canino">Canino</MenuItem>
                  <MenuItem value="Felino">Felino</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 12,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  select
                  label="Raza"
                  name="breed"
                  value={formData.breed}
                  onChange={handleChange}
                  required
                  disabled={!formData.species}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 140 }}
                  helperText={
                    !formData.species
                      ? 'Selecciona primero una especie'
                      : filteredBreeds.length === 0
                        ? 'No hay razas cargadas'
                        : `${filteredBreeds.length} razas disponibles`
                  }
                >
                  <MenuItem value="">Seleccionar raza...</MenuItem>
                  {filteredBreeds
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((breed) => (
                      <MenuItem key={breed.id} value={breed.name}>
                        {breed.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  select
                  label="Sexo"
                  name="genero"
                  value={formData.genero}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="">Macho o Hembra...</MenuItem>
                  <MenuItem value="Macho">Macho</MenuItem>
                  <MenuItem value="Hembra">Hembra</MenuItem>
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <TextField
                  fullWidth
                  label="Nacimiento"
                  name="birthDate"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={formData.birthDate}
                  onChange={handleChange}
                  required
                  sx={{ minWidth: 140 }}
                />
              </Grid>
            </Grid>

            {/* Datos de la Solicitud */}
            <Typography variant="h6" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Datos de la Solicitud</Typography>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  select
                  label="Necesidad de Atención *"
                  name="attentionNeed"
                  value={formData.attentionNeed}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    needs.length === 0
                      ? 'No hay necesidades cargadas'
                      : `${needs.length} necesidades disponibles`
                  }
                >
                  <MenuItem value="">Seleccionar necesidad...</MenuItem>
                  {needs
                    .filter((n) => n.isActive !== false)
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map((need) => (
                      <MenuItem key={need.id} value={need.name}>
                        {need.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <TextField
                  fullWidth
                  select
                  label="Prioridad"
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 140 }}
                >
                  <MenuItem value="">Seleccionar prioridad...</MenuItem>
                  <MenuItem value="Urgente">Urgente</MenuItem>
                  <MenuItem value="Alta">Alta</MenuItem>
                  <MenuItem value="Media">Media</MenuItem>
                </TextField>
              </Grid>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Información adicional *"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleChange}
                  multiline
                  minRows={2}
                  placeholder="Detalle brevemente el motivo de la consulta, antecedentes clínicos, etc."
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: { xs: 3, sm: 4 }, display: 'flex', justifyContent: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || !!rutError || !!phoneError}
                fullWidth
                sx={{ 
                  px: { xs: 4, sm: 6 },
                  maxWidth: { xs: '100%', sm: '400px' },
                  py: { xs: 1.5, sm: 1 },
                  fontSize: { xs: '1rem', sm: '0.9375rem' }
                }}
              >
                {loading ? <CircularProgress size={24} /> : 'Enviar Solicitud'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicUDLARequest;
